import { assetUrl } from "@/i18n/assets";
import { ScheduleServiceDialog } from "@/components/site/ScheduleServiceDialog";
import { useLocale } from "@/i18n/LocaleContext";
import { LanguageSwitch } from "@/components/site/LanguageSwitch";
import { stripLocale } from "@/i18n/locale";
import { useLocation } from "react-router-dom";
import { Link, NavLink } from "@/i18n/Link";
import { ChevronDown, Menu, Phone, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { SITE_PHONE_DISPLAY } from "@/routes-data";

type NavChild = { to: string; label: string };
type NavItem = { to: string; label: string; children?: NavChild[] };

const NAV: NavItem[] = [
  {
    to: "/products/",
    label: "Products",
    children: [
      { to: "/products/", label: "Products" },
      { to: "/dexter/", label: "Dexter" },
    ],
  },
  { to: "/axle-order-forms/", label: "Axle Order Forms" },
  {
    to: "/services/axle-repair/",
    label: "Trailer Repair",
    children: [
      { to: "/services/axle-repair/", label: "Trailer Repair" },
      { to: "/services/emergency-same-day-trailer-repair/", label: "Emergency & Same-Day Repair" },
      { to: "/free-51-point-inspection/", label: "Free 51-Point Inspection" },
      { to: "/trailer-axle-replacement-cost/", label: "Axle Replacement Cost" },
    ],
  },
  {
    to: "/about/",
    label: "About",
    children: [
      { to: "/about/", label: "About Us" },
      { to: "/reviews/", label: "Reviews" },
      { to: "/locations/", label: "Areas Served" },
      { to: "/blog/", label: "Blog" },
    ],
  },
  { to: "/contact/", label: "Contact" },
];

const NAV_ES: NavItem[] = [
  {
    to: "/products/",
    label: "Productos",
    children: [
      { to: "/products/", label: "Productos" },
      { to: "/dexter/", label: "Dexter" },
    ],
  },
  { to: "/axle-order-forms/", label: "Pedidos de Ejes" },
  {
    to: "/services/axle-repair/",
    label: "Reparación",
    children: [
      { to: "/services/axle-repair/", label: "Reparación de Remolques" },
      { to: "/services/emergency-same-day-trailer-repair/", label: "Reparación de Emergencia y el Mismo Día" },
      { to: "/free-51-point-inspection/", label: "Inspección Gratuita de 51 Puntos" },
      { to: "/trailer-axle-replacement-cost/", label: "Costo de Reemplazo de Ejes" },
    ],
  },
  {
    to: "/about/",
    label: "Nosotros",
    children: [
      { to: "/about/", label: "Quiénes Somos" },
      { to: "/reviews/", label: "Reseñas" },
      { to: "/locations/", label: "Áreas de Servicio" },
      { to: "/blog/", label: "Blog" },
    ],
  },
  { to: "/contact/", label: "Contacto" },
];

const HEADER_COPY = {
  en: {
    skip: "Skip to content",
    topline: "Authorized Dexter Distributor - Elkhart, IN",
    home: "Axle Inc. home",
    primary: "Primary",
    mobile: "Mobile",
    toggle: "Toggle menu",
    orderParts: "Order Parts",
    orderPartsSr: "(opens parts.axleinc.com in a new tab)",
    schedule: "Schedule Service",
  },
  es: {
    skip: "Saltar al contenido",
    topline: "Distribuidor Autorizado de Dexter - Elkhart, IN",
    home: "Inicio de Axle Inc.",
    primary: "Principal",
    mobile: "Móvil",
    toggle: "Abrir o cerrar el menú",
    orderParts: "Pedir Piezas",
    orderPartsSr: "(abre parts.axleinc.com, tienda en inglés, en una pestaña nueva)",
    schedule: "Programar Servicio",
  },
};

function isPathActive(pathname: string, target: string): boolean {
  const norm = (p: string) => (p.endsWith("/") ? p : `${p}/`);
  const a = norm(pathname);
  const b = norm(target);
  return a === b || a.startsWith(b);
}

export function Header() {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const { locale } = useLocale();
  const t = HEADER_COPY[locale];
  const nav = locale === "es" ? NAV_ES : NAV;
  const basePath = stripLocale(location.pathname);
  return (
    <>
      <a href="#main" className="skip-link sr-only focus:not-sr-only">
        {t.skip}
      </a>
      <div className="hidden md:block bg-secondary text-secondary-foreground/90 text-xs">
        <div className="container mx-auto px-4 h-11 flex items-center justify-between">
          <span className="tracking-wide uppercase">
            {t.topline}
          </span>
          <a
            href={`tel:${SITE_PHONE_DISPLAY.replace(/\D/g, "")}`}
            className="inline-flex items-center gap-2 text-lg font-bold text-white hover:text-white/80 transition-colors"
          >
            <Phone className="h-5 w-5" />
            {SITE_PHONE_DISPLAY}
          </a>
        </div>
      </div>
      <header className="sticky top-0 z-50 w-full border-b border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80">
        <div className="container mx-auto px-4 h-16 flex items-center justify-between gap-4 xl:gap-6">
          <Link to="/" className="flex items-center gap-2 shrink-0" aria-label={t.home}>
            <Logo />
          </Link>
          <nav aria-label={t.primary} className="hidden lg:flex items-center gap-4 xl:gap-7 whitespace-nowrap">
            {nav.map((n) => {
              if (n.children && n.children.length > 0) {
                const childActive = n.children.some((c) => isPathActive(basePath, c.to));
                return (
                  <HoverDropdown
                    key={n.to}
                    label={n.label}
                    items={n.children}
                    active={childActive}
                  />
                );
              }
              return (
                <NavLink
                  key={n.to}
                  to={n.to}
                  className={({ isActive }) =>
                    `text-sm font-medium transition-colors hover:text-primary ${
                      isActive ? "text-primary" : "text-foreground"
                    }`
                  }
                >
                  {n.label}
                </NavLink>
              );
            })}
          </nav>
          <div className="hidden lg:flex items-center gap-2 shrink-0">
            <span className="hidden xl:inline-flex"><LanguageSwitch /></span>
            <span className="inline-flex xl:hidden"><LanguageSwitch compact /></span>
            <Button
              asChild
              size="sm"
              className="bg-[#F6C445] text-secondary font-bold hover:bg-[#eeb832]"
            >
              <a href="https://parts.axleinc.com/" target="_blank" rel="noopener">
                {t.orderParts}
                <span className="sr-only"> {t.orderPartsSr}</span>
              </a>
            </Button>
            <ScheduleServiceDialog>
              <Button size="sm" className="bg-primary text-primary-foreground hover:bg-primary/90">
                {t.schedule}
              </Button>
            </ScheduleServiceDialog>
          </div>
          <div className="lg:hidden flex items-center gap-1">
          <LanguageSwitch compact />
          <button
            type="button"
            className="p-2 -mr-2"
            onClick={() => setOpen(!open)}
            aria-label={t.toggle}
            aria-expanded={open}
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
          </div>
        </div>
        {open && (
          <div className="lg:hidden border-t border-border bg-background">
            <nav aria-label={t.mobile} className="container mx-auto px-4 py-4 flex flex-col">
              {nav.map((n) => {
                if (n.children && n.children.length > 0) {
                  return (
                    <div key={n.to} className="border-b border-border">
                      <p className="pt-3 pb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                        {n.label}
                      </p>
                      <div className="flex flex-col pl-3">
                        {n.children.map((c) => (
                          <Link
                            key={c.to}
                            to={c.to}
                            onClick={() => setOpen(false)}
                            className="py-2.5 text-sm font-medium"
                          >
                            {c.label}
                          </Link>
                        ))}
                      </div>
                    </div>
                  );
                }
                return (
                  <Link
                    key={n.to}
                    to={n.to}
                    onClick={() => setOpen(false)}
                    className="py-2.5 text-sm font-medium border-b border-border last:border-0"
                  >
                    {n.label}
                  </Link>
                );
              })}
              <div className="flex flex-col gap-2 pt-4">
                <LanguageSwitch block onNavigate={() => setOpen(false)} />
                <Button
                  asChild
                  size="sm"
                  className="bg-[#F6C445] text-secondary font-bold hover:bg-[#eeb832]"
                >
                  <a href="https://parts.axleinc.com/" target="_blank" rel="noopener">
                    {t.orderParts}
                    <span className="sr-only"> {t.orderPartsSr}</span>
                  </a>
                </Button>
                <ScheduleServiceDialog>
                  <Button size="sm" className="bg-primary text-primary-foreground">{t.schedule}</Button>
                </ScheduleServiceDialog>
              </div>
            </nav>
          </div>
        )}
      </header>
    </>
  );
}

function HoverDropdown({
  label,
  items,
  active,
}: {
  label: string;
  items: NavChild[];
  active: boolean;
}) {
  const [open, setOpen] = useState(false);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const cancelClose = () => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  };
  const scheduleClose = () => {
    cancelClose();
    closeTimer.current = setTimeout(() => setOpen(false), 120);
  };
  const openNow = () => {
    cancelClose();
    setOpen(true);
  };

  useEffect(() => () => cancelClose(), []);

  return (
    <div onMouseEnter={openNow} onMouseLeave={scheduleClose}>
      <DropdownMenu open={open} onOpenChange={setOpen}>
        <DropdownMenuTrigger
          className={`inline-flex items-center gap-1 text-sm font-medium transition-colors hover:text-primary outline-none focus-visible:text-primary ${
            active ? "text-primary" : "text-foreground"
          }`}
        >
          {label}
          <ChevronDown
            className="h-3.5 w-3.5 transition-transform data-[state=open]:rotate-180"
            aria-hidden="true"
          />
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="start"
          className="min-w-[10rem]"
          onMouseEnter={openNow}
          onMouseLeave={scheduleClose}
        >
          {items.map((c) => (
            <DropdownMenuItem key={c.to} asChild>
              <NavLink
                to={c.to}
                className={({ isActive }) =>
                  `w-full cursor-pointer ${isActive ? "text-primary" : ""}`
                }
              >
                {c.label}
              </NavLink>
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}

function Logo() {
  return (
    <picture>
      <source srcSet={assetUrl("/brand/logo-red.webp")} type="image/webp" />
      <img
        src={assetUrl("/brand/logo-red.png")}
        alt="Axle Inc."
        width={1080}
        height={293}
        className="block h-9 w-[133px] md:h-10 md:w-[147px]"
        loading="eager"
        decoding="async"
      />
    </picture>
  );
}
